/**
 * PurchasesProvider — initializes RevenueCat on native platforms and exposes
 * entitlements + packages to the app. On web, it's a no-op shell that returns
 * empty data so consumers don't need platform branches.
 */
import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { isNativePlatform } from "@/mobile/platform";
import { useAuth } from "@/hooks/useAuth";

export interface PurchasePackage {
  identifier: string;
  product: { identifier: string; title: string; priceString: string; price: number };
}
export interface Entitlements {
  active: Record<string, { isActive: boolean; expiresDate?: string | null; productIdentifier: string }>;
}

interface Ctx {
  ready: boolean;
  packages: PurchasePackage[];
  entitlements: Entitlements;
  purchase: (pkg: PurchasePackage) => Promise<{ ok: boolean; error?: string }>;
  restore: () => Promise<{ ok: boolean; error?: string }>;
}

const PurchasesCtx = createContext<Ctx>({
  ready: false,
  packages: [],
  entitlements: { active: {} },
  purchase: async () => ({ ok: false, error: "not initialized" }),
  restore: async () => ({ ok: false, error: "not initialized" }),
});

export function PurchasesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [ready, setReady] = useState(false);
  const [packages, setPackages] = useState<PurchasePackage[]>([]);
  const [entitlements, setEntitlements] = useState<Entitlements>({ active: {} });

  useEffect(() => {
    if (!isNativePlatform()) { setReady(true); return; }
    const apiKey = import.meta.env.VITE_REVENUECAT_API_KEY as string | undefined;
    if (!apiKey) {
      console.warn("[IAP] VITE_REVENUECAT_API_KEY not set — skipping init");
      setReady(true);
      return;
    }
    (async () => {
      try {
        const { Purchases } = await import("@revenuecat/purchases-capacitor");
        await Purchases.configure({ apiKey, appUserID: user?.id ?? undefined });
        const offerings = await Purchases.getOfferings();
        const current = offerings.current;
        if (current?.availablePackages) {
          setPackages(current.availablePackages as unknown as PurchasePackage[]);
        }
        const info = await Purchases.getCustomerInfo();
        setEntitlements({ active: (info.customerInfo?.entitlements?.active ?? {}) as Entitlements["active"] });
      } catch (e) {
        console.warn("[IAP] init failed", e);
      } finally {
        setReady(true);
      }
    })();
  }, [user?.id]);

  const purchase = async (pkg: PurchasePackage): Promise<{ ok: boolean; error?: string }> => {
    if (!isNativePlatform()) return { ok: false, error: "native only" };
    try {
      const { Purchases } = await import("@revenuecat/purchases-capacitor");
      const res = await Purchases.purchasePackage({ aPackage: pkg as never });
      setEntitlements({ active: (res.customerInfo?.entitlements?.active ?? {}) as Entitlements["active"] });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: (e as Error).message };
    }
  };

  const restore = async (): Promise<{ ok: boolean; error?: string }> => {
    if (!isNativePlatform()) return { ok: false, error: "native only" };
    try {
      const { Purchases } = await import("@revenuecat/purchases-capacitor");
      const res = await Purchases.restorePurchases();
      setEntitlements({ active: (res.customerInfo?.entitlements?.active ?? {}) as Entitlements["active"] });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: (e as Error).message };
    }
  };

  return (
    <PurchasesCtx.Provider value={{ ready, packages, entitlements, purchase, restore }}>
      {children}
    </PurchasesCtx.Provider>
  );
}

export const usePurchases = () => useContext(PurchasesCtx);
export const useEntitlements = () => useContext(PurchasesCtx).entitlements;
export const usePackages = () => useContext(PurchasesCtx).packages;
